import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { PatternDegradationData } from '@/lib/case_workspace';
import { criterionDisplayName } from '@/lib/criterion_status';
import { cn } from '@/lib/utils';

export interface PatternDegradationBannerProps {
  degradation: PatternDegradationData | null;
  className?: string;
}

/**
 * Banner de qualidade insuficiente nas assinaturas-padrão (E25.F3 / ADR-0034 F). // ux-neutrality:allow doc-comment
 *
 * Renderiza quando o guard da análise sinaliza padrão com qualidade de // ux-neutrality:allow doc-comment
 * captura insuficiente (causa 3 do GT-2026-0014): os critérios dinâmicos // ux-neutrality:allow doc-comment
 * afetados aparecem como "Não avaliado" e o perito precisa saber que isso // ux-neutrality:allow doc-comment
 * decorre do material de referência, não de divergência. Espelha o // ux-neutrality:allow doc-comment
 * ReferenceSetInstabilityBanner: dismissível (estado local), render zero // ux-neutrality:allow doc-comment
 * sem degradação ou já dispensado; o chamador passa `key={analysis.id}` // ux-neutrality:allow doc-comment
 * (aviso por análise) para resetar o dismiss ao trocar de contestada // ux-neutrality:allow doc-comment
 * (lição F-E15.F4-01). // ux-neutrality:allow doc-comment
 */
export function PatternDegradationBanner({
  degradation,
  className,
}: PatternDegradationBannerProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);
  if (!degradation?.degraded || dismissed) return null;
  const criteria = degradation.affected_criteria.map(criterionDisplayName).join(', ');
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="pattern-degradation-banner"
      className={cn(
        'flex items-start gap-3 border border-warning/40 bg-warning-50 px-sc-4 py-sc-3 text-body-sm text-warning-700',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="font-medium">{ptBR.workspaceCaso.patternDegradationTitle}</p>
        <p className="mt-1">
          {ptBR.workspaceCaso.patternDegradationMessage}{' '}
          <span data-testid="pattern-degradation-criteria">({criteria})</span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="pattern-degradation-dismiss"
        aria-label={ptBR.workspaceCaso.patternDegradationDismiss}
        className="shrink-0 p-1 text-warning-700 transition-colors duration-fast ease-sc hover:bg-warning/20"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
